import { API_BASE_URL } from '@synergy/config';
import { Box, Stack, Tooltip } from '@mui/material';
import { ForwardToInbox } from '@mui/icons-material';
import { FormattedMessage } from 'react-intl';
import { NotificationOrigin, getNotificationOriginTranslationId } from '@synergy/models';
import { ReactElement, useEffect, useState } from 'react';

import { Api } from '../../../api';
import { NotificationOverride } from '../../api/account-api';

export interface PaloNotificationOverrideHintProps {
    /** The chosen customer or supplier, or nothing while none is chosen. */
    accountId?: number | null;
    /**
     * The notifications this document sends. A purchase order has two — one for
     * the shop and one for a manual order — and the mask cannot tell which one
     * this is, so both are named rather than guessed at.
     */
    origins: NotificationOrigin[];
}

/**
 * Says that this document's notification goes somewhere else.
 *
 * A customer or a supplier can have the recipient of a notification overridden.
 * The document gave no sign of it, so whoever sent it only learned afterwards
 * — from the mail log — that it had gone to a different address (#1895). The
 * mark sits beside the name, like the one for a charging supplier, and names
 * the address it will actually go to.
 */
export const PaloNotificationOverrideHint = ({
    accountId,
    origins,
}: PaloNotificationOverrideHintProps): ReactElement | null => {
    const [matches, setMatches] = useState<NotificationOverride[]>([]);
    const wanted = origins.join(',');

    useEffect(() => {
        if (!accountId) {
            setMatches([]);

            return;
        }

        let current = true;

        void new Api(API_BASE_URL).accounts.getNotificationOverrides(accountId).then(result => {
            if (!current) {
                return;
            }

            setMatches(
                result.success
                    ? result.result.filter(override => wanted.split(',').includes(override.notificationOrigin))
                    : []
            );
        });

        // An account chosen while the last answer is still on its way must not
        // be overwritten by it.
        return () => {
            current = false;
        };
    }, [accountId, wanted]);

    if (0 === matches.length) {
        return null;
    }

    return (
        <Tooltip
            title={
                <Stack gap={0.5}>
                    <FormattedMessage id="app.notification.sentToOtherAddress" />
                    {matches.map(override => (
                        <Box key={override.id}>
                            <FormattedMessage id={getNotificationOriginTranslationId(override.notificationOrigin)} />
                            {`: ${override.recipientEmail}`}
                        </Box>
                    ))}
                </Stack>
            }
            placement="top-end"
        >
            <Box sx={{ height: '20px', mr: 1, display: 'flex', alignItems: 'center' }}>
                <ForwardToInbox color="secondary" sx={{ fontSize: 20 }} />
            </Box>
        </Tooltip>
    );
};
